import { useEffect, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, Pencil } from 'lucide-react';
import { useFetchPickupSlots, useFetchUser, useUpdateSlotPreference } from '@/hooksApi';
import type { MerchantSetting, PreferredTiming, ReslotSummary } from '@/types';
import dayjs from '@/dayjsSetup';
import { getSlotIcon } from './slotIcons';
import { getWouldPickSlot } from './autoSelectPreview';
import SlotModal from './SlotModal';
import SlotChangeConfirmModal from './SlotChangeConfirmModal';
import { formatSlotWindow12 } from '@/utils/helpers';

// The single place a merchant sees and changes their default pickup
// time-slot preference — replaces the removed /settings/pickup-slot surface.
// Reads/writes the SAME data PickupSlotCard used to own:
// user.object.setting.pickup_time_slot_code matched against
// GET /merchants/pick-up-slots, saved via PUT .../preference
// (useUpdateSlotPreference). Only rendered on the Pickups tab.
const CurrentSlotBanner = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: user, isLoading: isUserLoading } = useFetchUser();
  const { data: slotsData, isLoading: isSlotsLoading } = useFetchPickupSlots();
  const { mutate: updatePreference, isLoading: isSaving } = useUpdateSlotPreference();

  const setting = user?.object?.setting as MerchantSetting | undefined;
  const currentCode = setting?.pickup_time_slot_code ?? undefined;
  const slots = slotsData?.slots ?? [];
  // The merchant's standing preferred timing bucket (null = Anytime) rides on
  // GET /user (setting.pickup_preferred_timing), NOT on the slots feed (that
  // stayed a backward-compatible bare array). It seeds the modal's timing row
  // when they re-pick their current slot, and drives the persistent
  // "Preferred timing" display line below.
  const savedPreferredTiming: PreferredTiming | null = setting?.pickup_preferred_timing ?? null;
  const currentSlot = slots.find((slot) => slot.code === currentCode);

  // The backend already flags a slot `is_next_day` dynamically based on
  // whether its window has elapsed for "today" — this is the exact same
  // per-slot signal the amber note everywhere else in this feature reuses,
  // so there is no separate time-cutoff calculation to duplicate here.
  const slotPassedToday = !!currentSlot?.is_next_day;

  // When the slot has passed for today AND the merchant has auto-select ON,
  // the pickup does NOT roll to tomorrow as long as a same-day slot still
  // remains — auto-select books that next same-day slot instead. Only then do
  // we promise "next slot"; if no same-day slot is left (getWouldPickSlot ===
  // null) it still rolls to tomorrow, so the amber strip keeps the tomorrow
  // copy. `getWouldPickSlot` trusts the server's per-slot `is_next_day` flag
  // (the exact slot auto-select would book), never a browser clock.
  const showNextSlotNotice =
    slotPassedToday && !!setting?.auto_select_next_slot && getWouldPickSlot(slots) != null;

  // v2-task-10fe (§4.3): the holiday-move notice — server-flag-only, same
  // rule as closing-soon: NEVER re-derive on the client. `moved_for_holiday`
  // is read straight off the merchant's PREFERRED slot (the one matching
  // setting.pickup_time_slot_code, i.e. `currentSlot` above), and the day
  // name is formatted from that slot's `pickup_date` via the app's dayjs
  // instance (locale already synced by useLanguageStore/setDayjsLanguage —
  // no hardcoded day names).
  const holidayMove = !!currentSlot?.moved_for_holiday;
  const holidayDayName = currentSlot?.pickup_date
    ? dayjs(currentSlot.pickup_date).format('dddd')
    : '';

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState<string | undefined>(undefined);
  // v2-task-9 (§4.1): the auto-select toggle is submitted together with the
  // slot choice in one Confirm call — seeded from the merchant's persisted
  // setting.auto_select_next_slot (absent/undefined defaults to OFF).
  const [autoSelectEnabled, setAutoSelectEnabled] = useState(false);
  // v2-task-10fe (§4.4): the recurring-pickup toggle, submitted together
  // with the slot choice + auto-select in the SAME Confirm call — seeded
  // from the merchant's persisted setting.recurring_pickup_enabled
  // (absent/undefined defaults to OFF).
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  // F1 (final-review Important fix): the toggles' as-opened ("seeded")
  // values, captured alongside autoSelectEnabled/recurringEnabled in
  // openModal so confirmDisabled can detect a toggle-only change. Without
  // this, a merchant who already has a preferred slot and only wants to
  // flip auto-select or recurring (the primary opt-in path for existing
  // merchants) would find Confirm permanently disabled.
  const [seededAutoSelect, setSeededAutoSelect] = useState(false);
  const [seededRecurring, setSeededRecurring] = useState(false);
  // Slot-change consolidation: switching to a DIFFERENT slot moves the
  // merchant's open same-day pickup (and all its orders) onto it, so the
  // Confirm click is parked here until they approve it in
  // SlotChangeConfirmModal. Same-slot saves (timing/toggle-only) skip the
  // gate — nothing moves.
  const [pendingChange, setPendingChange] = useState<{
    code: string;
    preferredTiming?: PreferredTiming | null;
  } | null>(null);

  useEffect(() => {
    if (!isModalOpen) {
      setSelectedCode(undefined);
    }
  }, [isModalOpen]);

  const openModal = (prefillCode?: string) => {
    setSelectedCode(prefillCode);
    setAutoSelectEnabled(!!setting?.auto_select_next_slot);
    setRecurringEnabled(!!setting?.recurring_pickup_enabled);
    setSeededAutoSelect(!!setting?.auto_select_next_slot);
    setSeededRecurring(!!setting?.recurring_pickup_enabled);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const applyPreference = (finalCode: string, preferredTiming?: PreferredTiming | null) => {
    // The new slot's human label, for the relocation warning copy.
    const newSlotLabel = slots.find((slot) => slot.code === finalCode)?.label ?? finalCode;
    updatePreference(
      {
        timeSlotCode: finalCode,
        autoSelectNextSlot: autoSelectEnabled,
        recurringPickupEnabled: recurringEnabled,
        preferredTiming,
      },
      {
        onSuccess: (reslotted: ReslotSummary) => {
          // Toast the actual outcome: already-routed pickups Fleet re-planned
          // onto the new slot (relocated → warning), open ones that followed
          // (moved), route-locked ones that couldn't move (skipped), or a plain
          // save. Relocation is surfaced first — it's the signal the merchant
          // most needs (their scheduled pickup agent is being re-assigned).
          if ((reslotted?.relocated ?? 0) > 0) {
            message.warning(
              String(
                t('slot_updated_relocated', { count: reslotted.relocated, slot: newSlotLabel }),
              ),
            );
          } else if (reslotted?.skipped > 0) {
            message.warning(String(t('slot_updated_partial_move', { count: reslotted.skipped })));
          } else if (reslotted?.moved > 0) {
            message.success(String(t('slot_updated_moved')));
          } else {
            message.success(String(t('slot_updated')));
          }
          // Preference is derived from GET /user (setting.pickup_time_slot_code)
          // — refresh it so the banner reflects the new choice immediately.
          queryClient.invalidateQueries(['user']);
          setPendingChange(null);
          closeModal();
        },
      },
    );
  };

  const handleConfirm = (code?: string, preferredTiming?: PreferredTiming | null) => {
    const finalCode = code ?? selectedCode;
    if (!finalCode) return;
    // Moving OFF an existing slot re-slots the merchant's open same-day
    // pickup — gate that behind an explicit approval. First-time selection
    // (no current slot) and same-slot saves have nothing to move.
    if (currentCode && finalCode !== currentCode) {
      setPendingChange({ code: finalCode, preferredTiming });
      return;
    }
    applyPreference(finalCode, preferredTiming);
  };

  if (isUserLoading || isSlotsLoading) return null;

  const bannerText = String(t('pickup_tomorrow_banner'));
  const splitIndex = bannerText.indexOf('. ');
  const leadSentence = splitIndex >= 0 ? bannerText.slice(0, splitIndex + 1) : bannerText;
  const trailingSentence = splitIndex >= 0 ? bannerText.slice(splitIndex + 2) : '';

  const SlotIcon = currentSlot ? getSlotIcon(currentSlot.code) : Clock;

  // The slot the parked (awaiting-approval) change is moving to — feeds the
  // confirm modal's body copy.
  const pendingSlot = pendingChange
    ? slots.find((slot) => slot.code === pendingChange.code)
    : undefined;

  return (
    <>
      {currentCode && currentSlot ? (
        <div
          // v2-task-8b (§2.3 shadow-raised token, explicit value so it never
          // drifts from Tailwind's own shadow-sm scale): matches the
          // empty-state card below, which used to have zero shadow.
          // v2-task-12fix (H1): `border`/`border-t` utilities only carry
          // border-WIDTH with Preflight off — `border-solid` restores the
          // visible outline on this card (and, below, on the CTA and the
          // amber notice strips' top divider).
          // The card keeps ONE neutral outline in every state. When the slot
          // has passed for today, only the amber NOTICE STRIP below turns
          // yellow — the card border/shadow stay neutral (no "everything goes
          // yellow"). The strip alone carries the warning.
          className='mb-5 overflow-hidden rounded-2xl border border-solid transition-shadow duration-200 hover:shadow-[0_6px_16px_rgba(15,15,15,0.10)]'
          style={{ borderColor: 'rgb(232,230,227)', background: '#fff' }}
        >
          <button
            type='button'
            onClick={() => openModal(currentCode)}
            className='group flex w-full items-stretch text-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]'
          >
            {/* Leading cluster: icon tile + eyebrow/active-pill + hero window. */}
            <div className='flex min-w-0 items-center gap-4 p-4'>
              <span
                className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl'
                style={{ background: 'rgb(237,224,74)' }}
              >
                <SlotIcon size={24} strokeWidth={1.8} color='rgb(57,57,60)' />
              </span>
              <div className='min-w-0'>
                <div className='flex items-center gap-2'>
                  <span className='text-[11px] font-semibold uppercase tracking-wider text-[rgb(115,123,140)]'>
                    {String(t('your_pickup_time_slot'))}
                  </span>
                  <span
                    className='inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold'
                    // Darker green text (vs. the shared rgb(39,155,101) status-pill
                    // color used elsewhere) — same AA contrast fix as SlotBadge:
                    // rgb(39,155,101) on this 12%-tint background falls short of
                    // AA at 10px, so this pill specifically uses a deeper shade.
                    style={{ background: 'rgba(49,196,127,0.12)', color: 'rgb(17,105,50)' }}
                  >
                    <CheckCircle2 size={11} /> {String(t('slot_active'))}
                  </span>
                </div>
                <div className='mt-1 flex items-baseline gap-2'>
                  <span className='text-xl font-extrabold tracking-tight'>
                    <bdi>
                      {formatSlotWindow12(currentSlot.window_from, currentSlot.window_to)}
                    </bdi>
                  </span>
                  <span className='text-sm font-medium text-[rgb(115,123,140)]'>
                    {currentSlot.label}
                  </span>
                </div>
                {/* §6.3 — persistent (not a tooltip) preferred-timing line under
                    the guaranteed window/label baseline. Always shows the
                    current setting (Anytime when unset); the best-effort note is
                    added only for a real bucket and is subdued (smaller/grey) so
                    it never reads as a second guarantee. */}
                <div className='mt-1 text-xs text-[rgb(57,57,60)]'>
                  {String(t('preferred_timing_label'))}:{' '}
                  <span className='font-semibold'>
                    {String(t(savedPreferredTiming ?? 'anytime'))}
                  </span>
                  {savedPreferredTiming ? (
                    <span className='text-[11px] text-[rgb(115,123,140)]'>
                      {' '}
                      ({String(t('preferred_timing_best_effort'))})
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Inset hairline divider between the slot cluster and the Next-pickup
                date. A FILLED w-px bar (not a border utility → sidesteps the
                Preflight border-solid trap) that is self-centered and short, so
                it floats with whitespace above/below and never touches the card's
                rounded corners — that's what made the old full-height border read
                as a boxed side. RTL-safe: a w-px bar has no directional property,
                its position is pure flex DOM-order so it auto-flips under dir=rtl. */}
            {currentSlot.pickup_date ? (
              <span
                aria-hidden='true'
                className='hidden w-px shrink-0 self-center md:block'
                style={{ height: '2.5rem', background: 'rgb(232,230,227)' }}
              />
            ) : null}

            {/* Middle "Next pickup" date — plain text, no box. Sits in the card's
                open centre as a light secondary detail beside the divider above.
                Hidden on narrow widths to avoid crowding; guarded on date. */}
            {currentSlot.pickup_date ? (
              <div className='hidden items-center px-6 md:flex'>
                <div>
                  <span className='text-[11px] font-semibold uppercase tracking-wider text-[rgb(115,123,140)]'>
                    {String(t('slot_next_pickup_label'))}
                  </span>
                  <div className='mt-1 text-sm font-semibold text-[rgb(57,57,60)]'>
                    <bdi>{dayjs(currentSlot.pickup_date).format('ddd, D MMM')}</bdi>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Trailing CTA, anchored to the inline-end edge. */}
            <div className='ms-auto flex items-center p-4'>
              <span
                className='inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-solid px-2.5 py-2 text-sm font-semibold transition-colors group-hover:border-transparent group-hover:bg-[rgb(237,224,74)] group-hover:text-[rgb(57,57,60)] sm:px-4'
                style={{ borderColor: 'rgb(232,230,227)' }}
              >
                <Pencil size={15} />
                <span className='hidden sm:inline'>{String(t('change_slot_cta'))}</span>
              </span>
            </div>
          </button>

          {slotPassedToday ? (
            showNextSlotNotice ? (
              // Auto-select ON + a same-day slot still available: the pickup
              // will be re-booked to that next SAME-DAY slot (not tomorrow).
              // Same amber strip, single-sentence copy, no clickable link —
              // there's nothing for the merchant to change here.
              <div className='flex items-start gap-2.5 border-t border-solid border-amber-200 bg-amber-50 px-4 py-3'>
                <span className='flex h-5 shrink-0 items-center'>
                  <AlertTriangle size={16} className='text-amber-500' />
                </span>
                <p className='m-0 text-xs leading-5 text-amber-700'>
                  {String(t('pickup_next_slot_banner'))}
                </p>
              </div>
            ) : (
              <div className='flex items-start gap-2.5 border-t border-solid border-amber-200 bg-amber-50 px-4 py-3'>
                {/* Icon boxed to the text's own line-height (h-5 = leading-5) and
                    centered within it, so its optical centre lands on the first
                    line's glyph centre in BOTH LTR and RTL — no by-eye mt nudge. */}
                <span className='flex h-5 shrink-0 items-center'>
                  <AlertTriangle size={16} className='text-amber-500' />
                </span>
                <p className='m-0 text-xs leading-5 text-amber-700'>
                  {leadSentence}{' '}
                  <button
                    type='button'
                    className='font-semibold underline hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]'
                    onClick={(event) => {
                      event.stopPropagation();
                      openModal(currentCode);
                    }}
                  >
                    {trailingSentence}
                  </button>
                </p>
              </div>
            )
          ) : null}

          {holidayMove ? (
            // When the "passed today" amber strip is already showing above,
            // drop this strip's top border so the two amber panels merge into
            // one continuous block instead of reading as a divided box.
            <div
              className={`flex items-start gap-2.5 bg-amber-50 px-4 py-3 ${
                slotPassedToday ? '' : 'border-t border-solid border-amber-200'
              }`}
            >
              <span className='flex h-5 shrink-0 items-center'>
                <AlertTriangle size={16} className='text-amber-500' />
              </span>
              <p className='m-0 text-xs leading-5 text-amber-700'>
                {String(t('slot_holiday_move_notice', { day: holidayDayName }))}
              </p>
            </div>
          ) : null}
        </div>
      ) : (
        <div
          // v2-task-8b (§2.3/§3.3): dashed empty-state border stays dashed
          // (deliberate "add a slot" affordance) but now shares the same
          // shadow-raised elevation as the filled state above, instead of
          // no shadow at all.
          className='mb-5 flex items-center gap-4 rounded-2xl border border-dashed p-4'
          style={{ borderColor: 'rgb(200,200,196)', background: '#fff' }}
        >
          <span
            className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl'
            style={{ background: 'rgb(245,245,243)' }}
          >
            <Clock size={24} color='rgb(115,123,140)' />
          </span>
          <div className='flex-1'>
            <p className='text-sm font-semibold'>{String(t('no_slot_selected_title'))}</p>
            <p className='mt-0.5 text-xs text-[rgb(115,123,140)]'>
              {String(t('no_slot_selected_body'))}
            </p>
          </div>
          <button
            type='button'
            onClick={() => openModal(undefined)}
            className='h-9 rounded-lg bg-[rgb(237,224,74)] px-5 text-sm font-semibold text-[rgb(57,57,60)] transition-colors hover:bg-[rgb(230,217,60)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]'
          >
            {String(t('select_slot_cta'))}
          </button>
        </div>
      )}

      <SlotModal
        open={isModalOpen}
        mode='preference'
        title={String(t(currentCode ? 'change_time_slot' : 'select_pickup_slot_title'))}
        subtitle={String(t('slot_modal_subtitle_preference'))}
        slots={slots}
        isLoading={isSlotsLoading}
        selectedCode={selectedCode}
        onChangeSelected={setSelectedCode}
        onConfirm={handleConfirm}
        onCancel={closeModal}
        currentSlotCode={currentCode}
        currentPref={savedPreferredTiming}
        isSaving={isSaving}
        confirmDisabled={
          selectedCode === currentCode &&
          autoSelectEnabled === seededAutoSelect &&
          recurringEnabled === seededRecurring
        }
        autoSelectEnabled={autoSelectEnabled}
        onChangeAutoSelect={setAutoSelectEnabled}
        recurringEnabled={recurringEnabled}
        onChangeRecurring={setRecurringEnabled}
      />

      {/* Mounted only while a change is parked — transitions are disabled on
          every slot modal anyway, and unmounting keeps the closed gate out of
          the DOM (antd otherwise leaves hidden modal content mounted). */}
      {pendingChange ? (
        <SlotChangeConfirmModal
          open
          slotLabel={pendingSlot?.label ?? ''}
          slotWindow={formatSlotWindow12(pendingSlot?.window_from, pendingSlot?.window_to)}
          isSaving={isSaving}
          onApprove={() => applyPreference(pendingChange.code, pendingChange.preferredTiming)}
          onCancel={() => setPendingChange(null)}
        />
      ) : null}
    </>
  );
};

export default CurrentSlotBanner;
